import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { MapContainer, TileLayer, Marker, Popup, Polyline, useMap } from 'react-leaflet';
import { RefreshCw, User, Truck, MapPin, Phone, Clock, X } from 'lucide-react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import MainService from '../../services/MainService';

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

const escapeHtml = (value) =>
  String(value || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');

/** Vehicle marker with driver name label on top (always visible). */
const createDriverIcon = (name, isActive) => {
  const label = escapeHtml(name || 'Driver');
  const bg = isActive ? '#10B981' : '#6B7280';
  return L.divIcon({
    className: 'custom-driver-marker',
    html: `
      <div style="display:flex;flex-direction:column;align-items:center;transform:translateY(-8px);">
        <div title="${label}" style="
          max-width:140px;
          padding:2px 8px;
          margin-bottom:4px;
          border-radius:999px;
          background:#111827;
          color:#fff;
          font-size:11px;
          font-weight:600;
          line-height:1.3;
          white-space:nowrap;
          overflow:hidden;
          text-overflow:ellipsis;
          box-shadow:0 1px 4px rgba(0,0,0,.35);
          pointer-events:none;
        ">${label}</div>
        <div style="
          width:40px;height:40px;border-radius:50%;
          background-color:${bg};border:3px solid white;
          box-shadow:0 2px 8px rgba(0,0,0,.3);
          display:flex;align-items:center;justify-content:center;
          font-size:18px;
        ">🚗</div>
      </div>
    `,
    iconSize: [140, 64],
    iconAnchor: [70, 56],
    popupAnchor: [0, -56],
  });
};

const createTrailPointIcon = (isLatest) =>
  L.divIcon({
    className: 'custom-trail-marker',
    html: `
      <div style="
        width:${isLatest ? 14 : 10}px;
        height:${isLatest ? 14 : 10}px;
        border-radius:50%;
        background:${isLatest ? '#46955A' : '#3B82F6'};
        border:2px solid white;
        box-shadow:0 1px 4px rgba(0,0,0,.3);
      "></div>
    `,
    iconSize: [isLatest ? 14 : 10, isLatest ? 14 : 10],
    iconAnchor: [isLatest ? 7 : 5, isLatest ? 7 : 5],
  });

const MapUpdater = ({ center, zoom }) => {
  const map = useMap();
  useEffect(() => {
    if (!center) return;
    map.setView(center, zoom);
  }, [map, center, zoom]);
  return null;
};

const FitBounds = ({ points }) => {
  const map = useMap();
  useEffect(() => {
    if (!points || points.length === 0) return;
    if (points.length === 1) {
      map.setView(points[0], 15);
      return;
    }
    map.fitBounds(L.latLngBounds(points), { padding: [40, 40], maxZoom: 16 });
  }, [map, points]);
  return null;
};

const normalizeDriver = (row) => {
  const lat = Number(row?.CurrentLocation?.lat ?? row?.LAT ?? 0);
  const lng = Number(row?.CurrentLocation?.lng ?? row?.LNG ?? 0);
  return {
    Id: row.Id,
    Name: row.Name || 'Unknown',
    DriverId: row.DriverId || String(row.Id),
    VehicleNumber: row.VehicleNumber || '-',
    Phone: row.Phone || '-',
    Status: row.Status || (row.IsActive ? 'Active' : 'Inactive'),
    IsActive: !!row.IsActive,
    CurrentLocation: {
      lat: Number.isFinite(lat) ? lat : 25.2048,
      lng: Number.isFinite(lng) ? lng : 55.2708,
    },
    LastUpdate: row.LastUpdate || '-',
    TotalDeliveries: row.TotalDeliveries ?? 0,
    TodayDeliveries: row.TodayDeliveries ?? 0,
    OnTimeRate: row.OnTimeRate || '-',
  };
};

const parseTrailPoints = (response) => {
  const list = Array.isArray(response)
    ? response
    : Array.isArray(response?.Data)
      ? response.Data
      : [];

  return list
    .map((row) => {
      const lat = Number(row.LAT ?? row.lat);
      const lng = Number(row.LNG ?? row.lng);
      if (!Number.isFinite(lat) || !Number.isFinite(lng) || (!lat && !lng)) return null;
      return {
        id: row.Id,
        lat,
        lng,
        time: row.Time || '',
        date: row.Date || '',
      };
    })
    .filter(Boolean);
};

const todayInput = () => new Date().toISOString().slice(0, 10);

const DriverTracking = () => {
  const [loading, setLoading] = useState(false);
  const [trailLoading, setTrailLoading] = useState(false);
  const [drivers, setDrivers] = useState([]);
  const [selectedDriver, setSelectedDriver] = useState(null);
  const [trailPoints, setTrailPoints] = useState([]);
  const [filterDate, setFilterDate] = useState(todayInput);
  const [mapCenter, setMapCenter] = useState([25.2048, 55.2708]);
  const [mapZoom, setMapZoom] = useState(12);
  const [mapReady, setMapReady] = useState(false);
  const [error, setError] = useState('');
  const mapRef = useRef(null);
  const selectedIdRef = useRef(null);

  useEffect(() => {
    selectedIdRef.current = selectedDriver?.Id ?? null;
  }, [selectedDriver?.Id]);

  const handleRefresh = useCallback(async (dateOverride) => {
    const date = dateOverride || filterDate;
    try {
      setLoading(true);
      setError('');
      const response = await MainService.GetDriverTracking(date);
      const list = Array.isArray(response)
        ? response
        : Array.isArray(response?.Data)
          ? response.Data
          : [];
      const mapped = list.map(normalizeDriver).filter((d) => d.CurrentLocation.lat && d.CurrentLocation.lng);
      setDrivers(mapped);

      const selectedId = selectedIdRef.current;
      if (selectedId) {
        const stillThere = mapped.find((d) => d.Id === selectedId);
        if (stillThere) {
          setSelectedDriver(stillThere);
        } else {
          setSelectedDriver(null);
          setTrailPoints([]);
        }
      }
    } catch (err) {
      console.error('GetDriverTracking error:', err);
      setError('Failed to load driver locations');
    } finally {
      setLoading(false);
    }
  }, [filterDate]);

  const loadTrail = useCallback(async (driver, dateOverride) => {
    if (!driver?.Id) {
      setTrailPoints([]);
      return;
    }
    const date = dateOverride || filterDate;
    try {
      setTrailLoading(true);
      const response = await MainService.GetEmployeeLocationHistory(driver.Id, date);
      const points = parseTrailPoints(response);
      setTrailPoints(points);
    } catch (err) {
      console.error('GetEmployeeLocationHistory error:', err);
      setTrailPoints([]);
      setError('Failed to load travel path');
    } finally {
      setTrailLoading(false);
    }
  }, [filterDate]);

  const selectDriver = useCallback(
    (driver) => {
      if (!driver) return;
      setSelectedDriver(driver);
      setMapCenter([driver.CurrentLocation.lat, driver.CurrentLocation.lng]);
      setMapZoom(14);
      loadTrail(driver, filterDate);
    },
    [loadTrail, filterDate]
  );

  const handleDateChange = (e) => {
    const next = e.target.value || todayInput();
    setFilterDate(next);
    setSelectedDriver(null);
    setTrailPoints([]);
  };

  const handleCloseSummary = () => {
    setSelectedDriver(null);
    setTrailPoints([]);
    setMapZoom(12);
    if (drivers.length > 0) {
      setMapCenter([drivers[0].CurrentLocation.lat, drivers[0].CurrentLocation.lng]);
    }
  };

  useEffect(() => {
    handleRefresh(filterDate);
    const interval = setInterval(() => {
      // Auto-refresh only when viewing today
      if (filterDate === todayInput()) {
        handleRefresh(filterDate);
      }
    }, 30000);
    return () => clearInterval(interval);
  }, [handleRefresh, filterDate]);

  useEffect(() => {
    if (selectedDriver?.Id) {
      loadTrail(selectedDriver, filterDate);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDriver?.Id, filterDate]);

  useEffect(() => {
    if (!error) return undefined;
    const timer = setTimeout(() => setError(''), 5000);
    return () => clearTimeout(timer);
  }, [error]);

  useEffect(() => {
    if (mapRef.current) {
      setTimeout(() => mapRef.current.invalidateSize(), 100);
    }
  }, [mapReady]);

  const visibleDrivers = useMemo(() => {
    if (!selectedDriver) return drivers;
    return drivers.filter((d) => d.Id === selectedDriver.Id);
  }, [drivers, selectedDriver]);

  const trailLatLngs = useMemo(
    () => trailPoints.map((p) => [p.lat, p.lng]),
    [trailPoints]
  );

  const fitPoints = useMemo(() => {
    if (!selectedDriver) return null;
    if (trailLatLngs.length > 0) return trailLatLngs;
    return [[selectedDriver.CurrentLocation.lat, selectedDriver.CurrentLocation.lng]];
  }, [selectedDriver, trailLatLngs]);

  const isToday = filterDate === todayInput();
  const filterDateLabel = (() => {
    try {
      return new Date(`${filterDate}T12:00:00`).toLocaleDateString('en-GB');
    } catch {
      return filterDate;
    }
  })();

  return (
    <div className="flex flex-col bg-gray-100" style={{ margin: '-24px', height: 'calc((100vh - 64px) * 0.9)', overflow: 'hidden' }}>
      <style>{`
        .custom-driver-marker,
        .custom-trail-marker {
          background: transparent !important;
          border: none !important;
        }
      `}</style>
      {error && (
        <div className="fixed top-4 right-4 z-[10000] flex min-w-[280px] items-center gap-3 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-red-700 shadow-lg">
          <span className="flex-1 text-sm">{error}</span>
          <button type="button" onClick={() => setError('')} className="text-red-500 hover:text-red-700">
            <X className="h-4 w-4" />
          </button>
        </div>
      )}

      <div className="bg-white border-b border-gray-200 p-4 shadow-sm flex-shrink-0">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center">
            <MapPin className="w-6 h-6 text-primary mr-2" />
            <h1 className="text-2xl font-bold text-gray-900">Driver Tracking</h1>
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            <label className="flex items-center gap-2 text-sm text-gray-700">
              <span className="font-medium whitespace-nowrap">Date</span>
              <input
                type="date"
                value={filterDate}
                max={todayInput()}
                onChange={handleDateChange}
                className="rounded-lg border border-gray-300 px-3 py-1.5 text-sm focus:border-[#46955A] focus:outline-none focus:ring-1 focus:ring-[#46955A]"
              />
            </label>
            {!isToday && (
              <button
                type="button"
                onClick={() => {
                  setFilterDate(todayInput());
                  setSelectedDriver(null);
                  setTrailPoints([]);
                }}
                className="text-sm text-[#46955A] hover:underline"
              >
                Today
              </button>
            )}
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-green-500" />
                <span className="text-sm text-gray-600">Active</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-gray-500" />
                <span className="text-sm text-gray-600">Inactive</span>
              </div>
            </div>
            <button
              type="button"
              onClick={() => handleRefresh(filterDate)}
              disabled={loading}
              className="flex items-center gap-2 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
            >
              <RefreshCw className={`w-4 h-4 ${loading || trailLoading ? 'animate-spin' : ''}`} />
              <span>Refresh</span>
            </button>
          </div>
        </div>
        <p className="text-sm text-gray-500 mt-2 ml-8">
          {selectedDriver
            ? `Travel path for ${selectedDriver.Name} on ${filterDateLabel}${trailPoints.length ? ` · ${trailPoints.length} points` : ''}`
            : `Driver locations for ${filterDateLabel}${isToday ? ' (live)' : ''}. Select a driver to view travel path.`}
        </p>
      </div>

      <div className="flex-1 relative" style={{ height: '100%', width: '100%', flex: '1 1 auto' }} id="map-container">
        <MapContainer
          key="driver-tracking-map"
          center={mapCenter}
          zoom={mapZoom}
          style={{ height: '100%', width: '100%', zIndex: 1 }}
          scrollWheelZoom
          whenReady={(map) => {
            mapRef.current = map.target;
            setMapReady(true);
            setTimeout(() => map.target.invalidateSize(), 200);
          }}
        >
          {!selectedDriver && <MapUpdater center={mapCenter} zoom={mapZoom} />}
          {selectedDriver && fitPoints && <FitBounds points={fitPoints} />}
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            maxZoom={19}
            updateWhenZooming={false}
            updateWhenIdle
          />

          {selectedDriver && trailLatLngs.length > 1 && (
            <Polyline
              positions={trailLatLngs}
              pathOptions={{ color: '#46955A', weight: 4, opacity: 0.85 }}
            />
          )}

          {selectedDriver &&
            trailPoints.map((point, index) => {
              const isLatest = index === trailPoints.length - 1;
              // Latest point shown as vehicle marker below — skip duplicate trail dot
              if (isLatest) return null;
              return (
                <Marker
                  key={point.id || `trail-${index}`}
                  position={[point.lat, point.lng]}
                  icon={createTrailPointIcon(false)}
                >
                  <Popup>
                    <div className="text-xs">
                      <p className="font-medium">{selectedDriver.Name}</p>
                      <p className="text-gray-600">{point.time || `Point ${index + 1}`}</p>
                    </div>
                  </Popup>
                </Marker>
              );
            })}

          {visibleDrivers.map((driver) => {
            const lastTrail = selectedDriver?.Id === driver.Id && trailPoints.length
              ? trailPoints[trailPoints.length - 1]
              : null;
            const position = lastTrail
              ? [lastTrail.lat, lastTrail.lng]
              : [driver.CurrentLocation.lat, driver.CurrentLocation.lng];
            return (
              <Marker
                key={driver.Id}
                position={position}
                icon={createDriverIcon(driver.Name, driver.IsActive)}
                eventHandlers={{
                  click: () => selectDriver(driver),
                }}
              >
                <Popup>
                  <div className="text-sm">
                    <p className="font-semibold">{driver.Name}</p>
                    <p className="text-gray-600">{driver.VehicleNumber}</p>
                    <p className={`text-xs ${driver.IsActive ? 'text-green-600' : 'text-gray-500'}`}>
                      {driver.Status}
                    </p>
                    {lastTrail?.time && (
                      <p className="text-xs text-gray-500 mt-1">{lastTrail.time}</p>
                    )}
                  </div>
                </Popup>
              </Marker>
            );
          })}
        </MapContainer>

        {selectedDriver && (
          <div className="absolute top-4 right-4 bg-white rounded-lg shadow-xl border border-gray-200 w-80 z-50 max-h-[calc(100vh-120px)] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 p-4 flex items-center justify-between">
              <h2 className="text-lg font-bold text-gray-900">Driver Summary</h2>
              <button type="button" onClick={handleCloseSummary} className="text-gray-400 hover:text-gray-600 transition-colors" title="Show all drivers">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-4 space-y-4">
              <div className="space-y-3">
                <div className="flex items-center gap-3 pb-3 border-b border-gray-200">
                  <div className={`w-12 h-12 rounded-full flex items-center justify-center ${selectedDriver.IsActive ? 'bg-green-100' : 'bg-gray-100'}`}>
                    <User className={`w-6 h-6 ${selectedDriver.IsActive ? 'text-green-600' : 'text-gray-600'}`} />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900">{selectedDriver.Name}</h3>
                    <p className="text-sm text-gray-500">ID: {selectedDriver.DriverId}</p>
                  </div>
                </div>

                <div className="flex items-center gap-3">
                  <Truck className="w-5 h-5 text-gray-400" />
                  <div>
                    <p className="text-sm font-medium text-gray-900">Vehicle</p>
                    <p className="text-sm text-gray-600">{selectedDriver.VehicleNumber}</p>
                  </div>
                </div>

                <div className="flex items-center gap-3">
                  <Phone className="w-5 h-5 text-gray-400" />
                  <div>
                    <p className="text-sm font-medium text-gray-900">Phone</p>
                    <p className="text-sm text-gray-600">{selectedDriver.Phone}</p>
                  </div>
                </div>

                <div className="flex items-center gap-3">
                  <div className={`w-3 h-3 rounded-full ${selectedDriver.IsActive ? 'bg-green-500' : 'bg-gray-500'}`} />
                  <div>
                    <p className="text-sm font-medium text-gray-900">Status</p>
                    <p className={`text-sm ${selectedDriver.IsActive ? 'text-green-600' : 'text-gray-600'}`}>
                      {selectedDriver.Status}
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-3">
                  <Clock className="w-5 h-5 text-gray-400" />
                  <div>
                    <p className="text-sm font-medium text-gray-900">Last Update</p>
                    <p className="text-sm text-gray-600">{selectedDriver.LastUpdate}</p>
                  </div>
                </div>
              </div>

              <div className="pt-4 border-t border-gray-200">
                <h4 className="text-sm font-semibold text-gray-900 mb-2">Travel path ({filterDateLabel})</h4>
                <p className="text-xs text-gray-600 mb-2">
                  {trailLoading ? 'Loading…' : `${trailPoints.length} location point(s)`}
                </p>
                <p className="text-xs text-gray-600">Lat: {selectedDriver.CurrentLocation.lat.toFixed(6)}</p>
                <p className="text-xs text-gray-600">Lng: {selectedDriver.CurrentLocation.lng.toFixed(6)}</p>
              </div>
            </div>
          </div>
        )}

        <div className="absolute top-4 left-4 bg-white rounded-lg shadow-xl border border-gray-200 w-64 z-50 max-h-[calc(100vh-120px)] overflow-y-auto">
          <div className="p-4 border-b border-gray-200 flex items-center justify-between gap-2">
            <h3 className="font-semibold text-gray-900">Drivers ({drivers.length})</h3>
            {selectedDriver && (
              <button
                type="button"
                onClick={handleCloseSummary}
                className="text-xs text-[#46955A] hover:underline"
              >
                Show all
              </button>
            )}
          </div>
          {drivers.length === 0 ? (
            <div className="p-4 text-sm text-gray-500">No driver locations for {filterDateLabel}.</div>
          ) : (
            <div className="divide-y divide-gray-200">
              {drivers.map((driver) => (
                <button
                  key={driver.Id}
                  type="button"
                  onClick={() => selectDriver(driver)}
                  className={`w-full p-3 text-left hover:bg-gray-50 transition-colors ${
                    selectedDriver?.Id === driver.Id ? 'bg-green-50 border-l-4 border-green-500' : ''
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <div className={`w-3 h-3 rounded-full ${driver.IsActive ? 'bg-green-500' : 'bg-gray-500'}`} />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">{driver.Name}</p>
                      <p className="text-xs text-gray-500 truncate">{driver.VehicleNumber}</p>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DriverTracking;
